import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import Seo from "@/components/Seo";
import ProviderLogo from "@/components/ProviderLogo";
import EnrollmentForm from "@/components/EnrollmentForm";
import useCurrentState from "@/routes/useCurrentState";
import { fetchPlan, fetchProvider } from "@/lib/api";
import { enrollmentApi } from "@/lib/enrollmentApi";
import { describeStatus } from "@/lib/enrollmentCopy";
import { statePath } from "@/lib/stateRegistry";

/**
 * /{state}/enroll/{planId}. One page, one <EnrollmentForm>, for every supplier.
 * The only supplier-specific input is `config.extension_fields`, declared by the
 * plan's connector and fetched through the enrollment adapter.
 */
export default function Enroll() {
  const { planId } = useParams();
  const state = useCurrentState();
  const navigate = useNavigate();

  const [plan, setPlan] = useState(null);
  const [provider, setProvider] = useState(null);
  const [config, setConfig] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await fetchPlan(planId);
        if (!p.states || !p.states.includes(state.code)) {
          navigate(statePath(state.slug, "plans"), { replace: true });
          return;
        }
        const [prov, cfg] = await Promise.all([fetchProvider(p.provider_id), enrollmentApi.getEnrollmentConfig(planId, state.code)]);
        if (cancelled) return;
        setPlan(p);
        setProvider(prov);
        setConfig(cfg);
      } catch {
        if (!cancelled) setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [planId, state, navigate]);

  // The server said the rate or documents the customer was shown are stale (QA-17): load the current plan and config so
  // the page shows the new rate and the form submits the new snapshot id. Throws if either fails; the form then says so.
  const reloadCurrent = async () => {
    const [p, cfg] = await Promise.all([fetchPlan(planId), enrollmentApi.getEnrollmentConfig(planId, state.code)]);
    setPlan(p);
    setConfig(cfg);
  };

  if (loadError) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-20" data-testid="enroll-load-error">
        <Seo title="Enroll" noindex />
        <p role="alert" className="text-lg">We could not load this enrollment page. Nothing was submitted.</p>
        <Link to={statePath(state.slug, "plans")} className="underline mt-4 inline-block">Back to plans</Link>
      </div>
    );
  }
  if (!plan || !config) return <div className="max-w-[1400px] mx-auto px-6 py-20 text-zinc-500" role="status">Loading…</div>;

  return (
    <div data-testid="enroll-page">
      <Seo title={`Enroll in ${plan.name}`} path={statePath(state.slug, `enroll/${plan.id}`)} noindex />
      <section className="border-b border-zinc-200">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-12 grid grid-cols-12 gap-8">
          <aside className="col-span-12 md:col-span-5">
            <p className="text-overline text-zinc-500 mb-4">You are enrolling in</p>
            <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <ProviderLogo provider={provider} size={48} />
                <div>
                  <p className="text-overline text-zinc-500">{provider && provider.name}</p>
                  <h2 className="font-display text-xl font-bold leading-tight">{plan.name}</h2>
                </div>
              </div>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div><dt className="text-zinc-500">Price at 1,000 kWh</dt><dd className="font-display font-bold">{plan.rate_1000.toFixed(1)}¢/kWh</dd></div>
                <div><dt className="text-zinc-500">Term</dt><dd className="font-display font-bold">{plan.term_months} months</dd></div>
              </dl>
              <Link to={statePath(state.slug, `plans/${plan.id}`)} className="text-xs underline mt-4 block">View plan details</Link>
            </div>
          </aside>

          <div className="col-span-12 md:col-span-7">
            {enrollmentApi.isMock && (
              <p role="note" className="rounded-lg border border-dashed border-slate-300 bg-slate-50 text-slate-600 p-3 text-xs mb-6" data-testid="mock-api-notice">
                Development mode: the enrollment service is simulated. Nothing you enter here is sent anywhere.
              </p>
            )}
            {done ? (
              <div className="rounded-3xl border border-slate-100 bg-white shadow-sm p-10" role="status" data-testid="enroll-success">
                <div className="w-14 h-14 rounded-2xl bg-[#DCFCE7] flex items-center justify-center mb-4">
                  <Check className="w-7 h-7 text-[#15803D]" aria-hidden="true" />
                </div>
                <h1 className="font-display text-3xl font-extrabold tracking-tight mb-3 text-[#0A2540]">Request received</h1>
                <p className="text-base">{describeStatus(done.status)}</p>
                <p className="text-base mt-2">Your switch may not take effect immediately. {provider && provider.name} will confirm your start date.</p>
                <div className="flex gap-3 mt-6 flex-wrap">
                  <Link to={statePath(state.slug, "plans")} className="btn-outline">Browse more plans</Link>
                  {done.next_step && done.next_step.type === "redirect" && done.next_step.url && (
                    <a href={done.next_step.url} rel="noreferrer" className="btn-primary" data-testid="enroll-continue-link">
                      Continue on {provider && provider.name}
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <>
                <h1 className="font-display text-4xl md:text-5xl tracking-tight font-extrabold mb-2 text-[#0A2540]">Enroll</h1>
                <p className="text-zinc-600 mb-8">Tell us where you need service. It takes a few minutes.</p>
                <EnrollmentForm
                  plan={plan}
                  provider={provider}
                  stateEntry={state}
                  extensionFields={config.extension_fields || []}
                  serverConfig={enrollmentApi.isMock ? null : config}
                  planRateSnapshotId={config.plan_rate_snapshot_id}
                  energyType={config.energy_type || plan.energy_type}
                  onRateChanged={reloadCurrent}
                  onStart={(args) => enrollmentApi.startEnrollment(args)}
                  onSubmit={(payload) => enrollmentApi.submitEnrollment(payload)}
                  onSubmitted={(result) => setDone(result)}
                />
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
